const DEFAULT_MAX_BYTES = 400_000;
const DEFAULT_MAX_DIMENSION = 1600;

/** Shopify product image targets (server also compresses; client reduces upload payload). */
export const SHOPIFY_MAX_BYTES = 1_000_000;
export const SHOPIFY_MAX_DIMENSION = 2048;

function loadImageElement(file: File): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
            URL.revokeObjectURL(url);
            resolve(img);
        };
        img.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error('Failed to load image'));
        };
        img.src = url;
    });
}

function canvasToBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
    return new Promise((resolve, reject) => {
        canvas.toBlob(
            (blob) => (blob ? resolve(blob) : reject(new Error('Image compression failed'))),
            'image/jpeg',
            quality
        );
    });
}

/** Resize/compress photos so uploads stay under Vercel's ~4.5MB body limit. */
export async function compressImageForUpload(
    file: File,
    maxBytes = DEFAULT_MAX_BYTES,
    maxDimension = DEFAULT_MAX_DIMENSION
): Promise<File> {
    if (!file.type.startsWith('image/')) {
        return file;
    }

    if (file.size <= maxBytes && file.type === 'image/jpeg') {
        return file;
    }

    const img = await loadImageElement(file);
    const scale = Math.min(1, maxDimension / Math.max(img.width, img.height));
    const width = Math.max(1, Math.round(img.width * scale));
    const height = Math.max(1, Math.round(img.height * scale));

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
        throw new Error('Canvas not supported');
    }
    ctx.drawImage(img, 0, 0, width, height);

    let quality = 0.85;
    let blob = await canvasToBlob(canvas, quality);

    while (blob.size > maxBytes && quality > 0.45) {
        quality -= 0.1;
        blob = await canvasToBlob(canvas, quality);
    }

    const baseName = file.name.replace(/\.[^.]+$/, '') || 'photo';
    return new File([blob], `${baseName}.jpg`, { type: 'image/jpeg', lastModified: Date.now() });
}

export async function compressImagesForUpload(files: File[]): Promise<File[]> {
    return Promise.all(files.map((file) => compressImageForUpload(file)));
}

/** Compress photos for Shopify publish (max 2048px, ~1MB, JPEG ~85%). */
export async function compressImageForShopify(file: File): Promise<File> {
    return compressImageForUpload(file, SHOPIFY_MAX_BYTES, SHOPIFY_MAX_DIMENSION);
}

export async function compressImagesForShopify(files: File[]): Promise<File[]> {
    return Promise.all(files.map((file) => compressImageForShopify(file)));
}

/** Vercel serverless request body cap (~4.5MB including multipart overhead). */
export const MAX_UPLOAD_BYTES = 4_000_000;

export function estimateUploadTooLarge(files: File[]): boolean {
    const total = files.reduce((sum, file) => sum + file.size, 0);
    return total > MAX_UPLOAD_BYTES;
}
